import { darken, lighten, getContrastRatio, alpha } from "@mui/material/styles";
const adjustSaturation = (color, minContrast = 4.5) => {
  let adjustedColor = color;
  let whiteContrast = getContrastRatio(adjustedColor, "#FFFFFF");
  while (whiteContrast < minContrast) {
    adjustedColor = darken(adjustedColor, 0.1);
    whiteContrast = getContrastRatio(adjustedColor, "#FFFFFF");
  }
  return adjustedColor;
};
const rgbStringToHsl = (rgb) => {
  const match = rgb.match(/rgb\((\d+),?\s*(\d+),?\s*(\d+)\)/);
  if (!match)
    return null;
  const r = parseInt(match[1], 10) / 255;
  const g = parseInt(match[2], 10) / 255;
  const b = parseInt(match[3], 10) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      case b:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};
const hslToHexString = (h, s, l) => {
  h = (h % 360 + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(h / 60 % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h >= 0 && h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h >= 60 && h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h >= 120 && h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h >= 180 && h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h >= 240 && h < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }
  const toHex = (val) => {
    const hex = Math.round((val + m) * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
const generateSecondaryColor = (rgbColor) => {
  const hslColor = rgbStringToHsl(rgbColor) || { h: 100, s: 18, l: 97 };
  hslColor.h = (hslColor.h - 120) % 360;
  hslColor.s = 75;
  hslColor.l = 50;
  const secondaryColor = hslToHexString(hslColor.h, hslColor.s, hslColor.l);
  return adjustSaturation(secondaryColor);
};
const generateColorPalette = (brandColor) => {
  const hexColorRegex = /^#([0-9A-F]{3}){1,2}$/i;
  const validColor = hexColorRegex.test(brandColor) ? brandColor : "#2196F3";
  const adjustedColor = adjustSaturation(validColor);
  const mainColor = darken(adjustedColor, 0.1);
  const primary = {
    main: mainColor,
    light: lighten(adjustedColor, 0.2),
    dark: darken(adjustedColor, 0.2),
    contrastText: getContrastRatio(adjustedColor, "#fff") >= 4.5 ? "#fff" : "#000",
    25: lighten(adjustedColor, 0.97),
    50: lighten(adjustedColor, 0.95),
    100: lighten(adjustedColor, 0.85),
    200: lighten(adjustedColor, 0.65),
    300: lighten(adjustedColor, 0.45),
    400: lighten(adjustedColor, 0.35),
    500: lighten(adjustedColor, 0.2),
    600: lighten(adjustedColor, 0.1),
    700: adjustedColor,
    800: mainColor,
    900: darken(adjustedColor, 0.2)
  };
  const adjustedSecondary = generateSecondaryColor(mainColor);
  const secondary = {
    main: adjustedSecondary,
    light: lighten(adjustedSecondary, 0.3),
    dark: darken(adjustedSecondary, 0.2),
    contrastText: getContrastRatio(adjustedSecondary, "#fff") >= 4.5 ? "#fff" : "#000",
    25: lighten(adjustedSecondary, 0.97),
    50: lighten(adjustedSecondary, 0.95),
    100: lighten(adjustedSecondary, 0.85),
    200: lighten(adjustedSecondary, 0.65),
    300: lighten(adjustedSecondary, 0.45),
    400: lighten(adjustedSecondary, 0.25),
    500: adjustedSecondary,
    600: darken(adjustedSecondary, 0.15),
    700: darken(adjustedSecondary, 0.3),
    800: darken(adjustedSecondary, 0.45),
    900: darken(adjustedSecondary, 0.6)
  };
  const background = {
    default: alpha(adjustedColor, 0.05)
  };
  return { primary, secondary, background };
};
export {
  generateColorPalette
};
